import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  collection,
  getDocs,
  doc,
  getDoc,
  addDoc,
  serverTimestamp,
  query,
  where,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import { useAuth } from "../context/AuthContext"; // Import auth context
import EnrollmentModal from "../components/EnrollmentModal";
import ErrorMessage from "../components/common/ErrorMessage";

const ClassroomPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth(); // Get current user

  // States for data
  const [classroomPrograms, setClassroomPrograms] = useState([]);
  const [facultyProfiles, setFacultyProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userEnrollments, setUserEnrollments] = useState([]); // Store user's active enrollments

  // UI states
  const [activeTab, setActiveTab] = useState(null);
  const [activeFaculty, setActiveFaculty] = useState(null);
  const [showAllFaculty, setShowAllFaculty] = useState(false);
  const [testimonials, setTestimonials] = useState([]);
  const [enrollmentModalOpen, setEnrollmentModalOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [activeSubscriptionDetails, setActiveSubscriptionDetails] =
    useState(null);
  const [demoFormData, setDemoFormData] = useState({
    studentName: "",
    parentEmail: "",
    phone: "",
    grade: "",
    program: "",
    date: "",
    message: "",
  });
  const [demoFormSubmitting, setDemoFormSubmitting] = useState(false);
  const [demoFormSuccess, setDemoFormSuccess] = useState(false);
  const [demoFormError, setDemoFormError] = useState(null);

  // Fetch data from Firestore on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
    fetchData();
  }, []);

  // Fetch user enrollments when user changes
  useEffect(() => {
    if (currentUser) {
      fetchUserEnrollments();
    } else {
      setUserEnrollments([]);
    }
  }, [currentUser]);

  // Function to fetch user's active enrollments
  const fetchUserEnrollments = async () => {
    if (!currentUser) return;

    try {
      const enrollmentsQuery = query(
        collection(db, "enrollments"),
        where("userId", "==", currentUser.uid),
        where("status", "==", "completed")
      );

      const enrollmentsSnapshot = await getDocs(enrollmentsQuery);
      const enrollmentsData = enrollmentsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Filter only active (non-expired) enrollments
      const currentDate = new Date();
      const activeEnrollments = enrollmentsData.filter((enrollment) => {
        if (enrollment.expiryDate) {
          // Check if expiryDate is a Firestore Timestamp
          const expiryDate = enrollment.expiryDate.toDate
            ? enrollment.expiryDate.toDate()
            : new Date(enrollment.expiryDate);
          return expiryDate > currentDate;
        }
        return false;
      });

      setUserEnrollments(activeEnrollments);
    } catch (err) {
      console.error("Error fetching user enrollments:", err);
    }
  };

  // Function to check if user has active subscription for a course
  const hasActiveSubscription = (courseId) => {
    return userEnrollments.some(
      (enrollment) => enrollment.courseId === courseId
    );
  };

  // Function to get active subscription details for a course
  const getActiveSubscriptionDetails = (courseId) => {
    return userEnrollments.find(
      (enrollment) => enrollment.courseId === courseId
    );
  };

  // Function to fetch program and faculty data from Firestore
  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch programs
      const programsSnapshot = await getDocs(
        collection(db, "classroomPrograms")
      );
      const programsData = programsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Fetch faculty profiles
      const facultySnapshot = await getDocs(collection(db, "facultyProfiles"));
      const facultyData = facultySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Fetch testimonials
      const testimonialsSnapshot = await getDocs(
        collection(db, "testimonials")
      );
      const testimonialsData = testimonialsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Set the data to state
      setClassroomPrograms(programsData);
      setFacultyProfiles(facultyData);
      setTestimonials(testimonialsData);

      // Set the first program as active if programs exist
      if (programsData.length > 0) {
        setActiveTab(programsData[0].id);
      }

      setLoading(false);
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("Failed to load data. Please refresh the page.");
      setLoading(false);
    }
  };

  // Function to handle enrollment button click
  const handleEnrollClick = (course) => {
    // Check if user is logged in
    if (!currentUser) {
      // Redirect to login/register page
      navigate("/login?redirect=classroom");
      return;
    }

    // Check if user has active subscription for this course
    if (hasActiveSubscription(course.id)) {
      const subscriptionDetails = getActiveSubscriptionDetails(course.id);
      setActiveSubscriptionDetails(subscriptionDetails);
      setShowSubscriptionModal(true);
      return;
    }

    // Proceed with normal enrollment
    setSelectedCourse(course);
    setEnrollmentModalOpen(true);
  };

  // Close enrollment modal
  const closeEnrollmentModal = () => {
    setEnrollmentModalOpen(false);
    setSelectedCourse(null);
  };

  // Close subscription modal
  const closeSubscriptionModal = () => {
    setShowSubscriptionModal(false);
    setActiveSubscriptionDetails(null);
  };

  // Format date for display
  const formatDate = (date) => {
    if (!date) return "N/A";
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    return dateObj.toLocaleDateString("en-IN", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Active Subscription Modal Component
  const ActiveSubscriptionModal = () => (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          aria-hidden="true"
          onClick={closeSubscriptionModal}
        ></div>

        <span
          className="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >
          &#8203;
        </span>

        <div className="inline-block px-4 pt-5 pb-4 overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:align-middle sm:max-w-md sm:w-full sm:p-6">
          <div>
            <div className="flex items-center justify-center w-12 h-12 mx-auto bg-blue-100 rounded-full">
              <svg
                className="w-6 h-6 text-blue-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div className="mt-3 text-center sm:mt-5">
              <h3
                className="text-lg font-medium leading-6 text-gray-900"
                id="modal-title"
              >
                Active Subscription Found
              </h3>
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  You already have an active subscription for this course.
                </p>
              </div>
            </div>
          </div>

          {activeSubscriptionDetails && (
            <div className="mt-5">
              <div className="bg-gray-50 p-4 rounded-md space-y-3">
                <div className="text-center">
                  <h4 className="text-base font-medium text-gray-900 mb-2">
                    Subscription Details
                  </h4>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Course:</span>
                    <span className="font-medium text-gray-900">
                      {activeSubscriptionDetails.courseTitle}
                    </span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-gray-500">Student:</span>
                    <span className="font-medium text-gray-900">
                      {activeSubscriptionDetails.studentName}
                    </span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-gray-500">Enrollment Date:</span>
                    <span className="font-medium text-gray-900">
                      {formatDate(activeSubscriptionDetails.enrollmentDate)}
                    </span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-gray-500">Expiry Date:</span>
                    <span className="font-medium text-gray-900">
                      {formatDate(activeSubscriptionDetails.expiryDate)}
                    </span>
                  </div>

                  {activeSubscriptionDetails.language && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Language:</span>
                      <span className="font-medium text-gray-900">
                        {activeSubscriptionDetails.language}
                      </span>
                    </div>
                  )}

                  <div className="border-t border-gray-200 my-2 pt-2"></div>

                  <div className="flex justify-between">
                    <span className="text-gray-500">Payment Type:</span>
                    <span className="font-medium text-gray-900">
                      {activeSubscriptionDetails.paymentType === "full"
                        ? "Full Payment"
                        : "Installments"}
                    </span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-gray-500">Total Amount:</span>
                    <span className="font-medium text-gray-900">
                      ₹{activeSubscriptionDetails.totalAmount?.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg
                      className="w-5 h-5 text-green-400"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-green-700">
                      Your subscription is active and you have full access to
                      this course. You can continue your learning from where you
                      left off.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
            <button
              type="button"
              className="inline-flex justify-center w-full px-4 py-2 text-base font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:col-start-2 sm:text-sm"
              onClick={() => {
                closeSubscriptionModal();
                navigate("/dashboard"); // Redirect to dashboard
              }}
            >
              Go to Dashboard
            </button>
            <button
              type="button"
              className="inline-flex justify-center w-full px-4 py-2 mt-3 text-base font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:mt-0 sm:col-start-1 sm:text-sm"
              onClick={closeSubscriptionModal}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Handle demo form input changes
  const handleDemoInputChange = (e) => {
    const { id, value } = e.target;
    setDemoFormData({
      ...demoFormData,
      [id]: value,
    });
  };

  // Handle demo form submission
  const handleDemoFormSubmit = async (e) => {
    e.preventDefault();

    // Simple validation
    if (
      !demoFormData.studentName ||
      !demoFormData.parentEmail ||
      !demoFormData.phone ||
      !demoFormData.grade ||
      !demoFormData.program
    ) {
      setDemoFormError("Please fill all required fields");
      return;
    }

    setDemoFormSubmitting(true);
    setDemoFormError(null);

    try {
      // Add document to Firestore
      await addDoc(collection(db, "demoClassRequests"), {
        ...demoFormData,
        timestamp: serverTimestamp(),
        status: "new",
      });

      // Reset form and show success
      setDemoFormData({
        studentName: "",
        parentEmail: "",
        phone: "",
        grade: "",
        program: "",
        date: "",
        message: "",
      });
      setDemoFormSuccess(true);

      // Reset success message after 5 seconds
      setTimeout(() => {
        setDemoFormSuccess(false);
      }, 5000);
    } catch (err) {
      console.error("Error saving form data:", err);
      setDemoFormError("Failed to submit your request. Please try again.");
    } finally {
      setDemoFormSubmitting(false);
    }
  };

  // Teaching methodology
  const teachingMethodology = [
    {
      id: 1,
      title: "Interactive Learning",
      description:
        "Live interactive sessions with real-time doubt clearing and engagement through advanced digital tools and collaborative learning.",
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
          />
        </svg>
      ),
    },
    {
      id: 2,
      title: "Personalized Attention",
      description:
        "Small batch sizes ensure every student gets personalized guidance, individual progress tracking, and customized learning plans.",
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
          />
        </svg>
      ),
    },
    {
      id: 3,
      title: "Regular Assessments",
      description:
        "Continuous evaluation through weekly tests, mock exams, and performance analytics to identify areas for improvement.",
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
          />
        </svg>
      ),
    },
    {
      id: 4,
      title: "Expert Faculty",
      description:
        "Learn from experienced educators with proven track records in school education and competitive exam preparation.",
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
          />
        </svg>
      ),
    },
  ];

  // Contact points
  const centers = [
    {
      id: 1,
      name: "Technical Support Center",
      address: "Available Monday to Saturday, 9 AM - 8 PM",
      phone: "+91 9916023502",
      email: "support@skysep.com",
      facilities: [
        "Technical Assistance",
        "Learning Platform Support",
        "Device Compatibility Help",
      ],
      image: require("../assets/classrooms/hq3.jpg"),
    },
    {
      id: 2,
      name: "Parent Connect Desk",
      address: "For queries and counseling sessions",
      phone: "+91 9916023502",
      email: "parents@skysep.com",
      facilities: [
        "Progress Reports",
        "Counseling Sessions",
        "Feedback Mechanism",
      ],
      image: require("../assets/classrooms/hq4.jpg"),
    },
  ];

  // FAQs - Placeholder for now, can be updated with Firestore data later
  const faqs = [
    {
      question: "What is the batch size for classes?",
      answer:
        "We maintain small batch sizes of 20-25 students to ensure personalized attention and interactive learning experience.",
    },
    {
      question: "Are the classes live or recorded?",
      answer:
        "We offer live interactive classes with experienced faculty. All sessions are also recorded for later revision.",
    },
    {
      question: "What if I miss a class?",
      answer:
        "You can access the recorded session anytime through our learning portal. Additionally, our faculty provides extra support during doubt clearing sessions.",
    },
    {
      question: "Do you provide study material?",
      answer:
        "Yes, we provide comprehensive study material including books, practice papers, and mock tests for all our courses.",
    },
    {
      question: "How do you handle doubt clearing?",
      answer:
        "We have dedicated doubt clearing sessions after each class, plus a 24/7 online doubt resolution system where students can get their queries answered.",
    },
    {
      question: "Is there any scholarship available?",
      answer:
        "Yes, we offer merit-based scholarships based on entrance test performance. Top performers can get up to 50% scholarship.",
    },
    {
      question:
        "How are the entrance exam preparations different from regular classes?",
      answer:
        "Entrance exam preparations include specialized training for specific exam patterns, intensive practice sessions, and mock tests designed according to the actual exam format.",
    },
  ];

  // Default programs if Firestore data is not loaded yet
  const defaultPrograms = [
    {
      id: "665d75a7e0f5a98bdf3c8a01",
      title: "10th Standard English Medium",
      duration: "Full Academic Year",
      language: "English",
      batches: "Weekday & Weekend",
      features: [
        "Complete CBSE/State Board Curriculum Coverage",
        "Interactive Classes",
        "Regular Doubt Clearing Sessions",
        "Weekly Tests & Assessments",
        "Comprehensive Study Material",
        "Parent-Teacher Meetings",
        "Performance Analytics Dashboard",
      ],
      price: "₹25,000/year",
      startDates: ["July 15, 2025", "August 1, 2025", "September 1, 2025"],
      image:
        "https://images.unsplash.com/photo-1509062522246-3755977927d7?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&h=750&q=80",
      popular: true,
    },
    {
      id: "665d75a7e0f5a98bdf3c8a02",
      title: "10th Standard Kannada Medium",
      language: "Kannada",
      duration: "Full Academic Year",
      batches: "Specialized Batches",
      features: [
        "State Board Focused Preparation",
        "Native Language Instruction",
        "Previous Year Question Papers",
        "Mock Tests & Practice Sessions",
        "Comprehensive Study Plan",
        "Result-Oriented Approach",
      ],
      price: "₹20,000/year",
      startDates: ["July 15, 2025", "August 1, 2025"],
      image:
        "https://images.unsplash.com/photo-1577896851231-70ef18881754?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&h=750&q=80",
      popular: false,
    },
    {
      id: "665d75a7e0f5a98bdf3c8a03",
      title: "10th Standard NCERT (Maths + Science)",
      language: "English",
      duration: "Full Academic Year",
      batches: "Weekend Special",
      features: [
        "NCERT Curriculum Coverage",
        "Focus on Mathematical Concepts",
        "Science Practical Knowledge",
        "Regular Assessments",
        "Doubt Clearing Sessions",
        "Foundation for Competitive Exams",
      ],
      price: "₹22,000/year",
      startDates: ["July 20, 2025", "August 10, 2025"],
      image:
        "https://images.unsplash.com/photo-1580582932707-520aed937b7b?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&h=750&q=80",
      popular: false,
    },
    {
      id: "665d75a7e0f5a98bdf3c8a04",
      title: "Navodaya Entrance Preparation",
      duration: "6 months",
      language: "English",
      batches: "Specialized Batches",
      features: [
        "Jawahar Navodaya Vidyalaya (JNV) Training",
        "Previous Year Question Papers",
        "Mock Tests & Practice Sessions",
        "Interview Preparation",
        "Personality Development",
        "Result-Oriented Approach",
      ],
      price: "₹18,000",
      startDates: ["January 15, 2025", "February 15, 2025"],
      image:
        "https://images.unsplash.com/photo-1509062522246-3755977927d7?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&h=750&q=80",
      popular: true,
    },
    {
      id: "665d75a7e0f5a98bdf3c8a05",
      title: "Morarji Entrance Preparation",
      duration: "6 months",
      language: "English",
      batches: "Specialized Batches",
      features: [
        "Morarji Desai School Focused Preparation",
        "Previous Year Question Papers",
        "Mock Tests & Practice Sessions",
        "Interview & Personality Development",
        "Comprehensive Study Plan",
        "Result-Oriented Approach",
      ],
      price: "₹18,000",
      startDates: ["January 15, 2025", "February 15, 2025"],
      image:
        "https://images.unsplash.com/photo-1580582932707-520aed937b7b?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&h=750&q=80",
      popular: false,
    },
  ];

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex justify-center items-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#2C5282]"></div>
        <p className="ml-3 text-[#2C5282] font-medium">Loading programs...</p>
      </div>
    );
  }

  // Show error state
  if (error) {
    return <ErrorMessage message={error} />;
  }

  // Use Firestore data if available, otherwise use default programs
  const displayPrograms =
    classroomPrograms.length > 0 ? classroomPrograms : defaultPrograms;

  return (
    <div className="pt-20">
      {enrollmentModalOpen && selectedCourse && (
        <EnrollmentModal
          isOpen={enrollmentModalOpen}
          onClose={closeEnrollmentModal}
          courseDetails={selectedCourse}
        />
      )}

      {showSubscriptionModal && <ActiveSubscriptionModal />}

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#2C5282] to-[#1A365D] text-white">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-6">
                Excellence in Education
              </h1>
              <p className="text-lg md:text-xl mb-8 text-blue-100">
                Empowering students with comprehensive education for academic
                excellence and competitive exam success.
              </p>
              <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
                <a
                  href="#programs-display-section"
                  className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-[#2C5282] bg-white hover:bg-gray-100 transition-colors duration-300 shadow-md"
                >
                  Enroll Now
                </a>
                <a
                  href="#schedule-visit"
                  className="inline-flex items-center justify-center px-6 py-3 border-2 border-white text-base font-medium rounded-md text-white hover:bg-white/10 transition-colors duration-300"
                >
                  Book Free Demo Class
                </a>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="hidden md:block relative"
            >
              <img
                src="/images/classroom-hero.jpg"
                alt="Classroom Learning"
                className="rounded-lg shadow-2xl w-full"
              />
              <div className="absolute -bottom-6 -right-6 bg-white text-[#2C5282] rounded-lg p-4 shadow-xl">
                <p className="text-sm font-bold">SUCCESS RATE</p>
                <p className="text-2xl font-extrabold">92%+</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Programs Tabs */}
      <section className="py-16 bg-white" id="programs-display-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.h2
              className="text-3xl font-extrabold text-[#2C5282] sm:text-4xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Our Academic Programs
            </motion.h2>
            <motion.p
              className="mt-4 max-w-2xl text-xl text-gray-600 mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              Comprehensive courses for academic excellence and entrance exam
              preparation
            </motion.p>
          </div>

          {/* Program Tabs */}
          <div className="flex flex-wrap justify-center mb-12">
            {displayPrograms.map((program) => (
              <button
                key={program.id}
                onClick={() => setActiveTab(program.id)}
                className={`px-6 py-3 m-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === program.id
                    ? "bg-[#2C5282] text-white shadow-md"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {program.title}
                {hasActiveSubscription(program.id) && (
                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                    Active
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Active Program Details */}
          {displayPrograms.map((program) => (
            <div
              key={program.id}
              className={`${
                activeTab === program.id ? "block" : "hidden"
              } bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200`}
            >
              <div className="grid grid-cols-1 lg:grid-cols-2">
                <div className="relative h-64 lg:h-auto">
                  <img
                    src={program.image}
                    alt={program.title}
                    className="w-full h-full object-cover"
                  />
                  {program.popular && (
                    <div className="absolute top-4 right-4 bg-[#2C5282] text-white px-4 py-1 rounded-full text-sm font-bold shadow-md">
                      Most Popular
                    </div>
                  )}
                  {hasActiveSubscription(program.id) && (
                    <div className="absolute top-4 left-4 bg-green-600 text-white px-4 py-1 rounded-full text-sm font-bold shadow-md">
                      Subscribed
                    </div>
                  )}
                </div>
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-[#2C5282] mb-4">
                    {program.title}
                  </h3>
                  <div className="flex flex-wrap gap-4 mb-6">
                    <div className="flex items-center text-gray-700">
                      <svg
                        className="w-5 h-5 text-[#2C5282] mr-2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span>Duration: {program.duration}</span>
                    </div>
                    <div className="flex items-center text-gray-700">
                      <svg
                        className="w-5 h-5 text-[#2C5282] mr-2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                        />
                      </svg>
                      <span>Batches: {program.batches}</span>
                    </div>
                    <div className="flex items-center text-gray-700">
                      <svg
                        className="w-5 h-5 text-[#2C5282] mr-2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span>Fee: {program.price}</span>
                    </div>
                  </div>

                  <h4 className="text-lg font-medium text-[#2C5282] mb-3">
                    Key Features:
                  </h4>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-4 mb-6">
                    {program.features.map((feature, i) => (
                      <li key={i} className="flex items-start">
                        <svg
                          className="h-5 w-5 text-[#2C5282] mt-0.5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        <span className="ml-2 text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <h4 className="text-lg font-medium text-[#2C5282] mb-3">
                    Upcoming Batches:
                  </h4>
                  <ul className="mb-6">
                    {program.startDates.map((date, i) => (
                      <li key={i} className="flex items-center mb-2">
                        <svg
                          className="w-5 h-5 text-[#2C5282] mr-2"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                        <span className="text-gray-700">{date}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
                    {hasActiveSubscription(program.id) ? (
                      <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
                        <button
                          onClick={() => handleEnrollClick(program)}
                          className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-green-600 hover:bg-green-700 transition-colors duration-300 shadow-md"
                        >
                          View Subscription
                        </button>
                        <Link
                          to="/dashboard"
                          className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-300"
                        >
                          Go to Dashboard
                        </Link>
                      </div>
                    ) : (
                      <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
                        <button
                          onClick={() => handleEnrollClick(program)}
                          className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-[#2C5282] hover:bg-[#1A365D] transition-colors duration-300 shadow-md"
                        >
                          Enroll Now
                        </button>
                        <Link
                          to="/contact?program=classroom"
                          className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-300"
                        >
                          Request Brochure
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Teaching Methodology */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.h2
              className="text-3xl font-extrabold text-[#2C5282] sm:text-4xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Our Teaching Methodology
            </motion.h2>
            <motion.p
              className="mt-4 max-w-2xl text-xl text-gray-600 mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              A proven approach that ensures comprehensive learning and academic
              excellence
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {teachingMethodology.map((method, index) => (
              <motion.div
                key={method.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 text-center"
              >
                <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 text-[#2C5282] mb-4 mx-auto">
                  {method.icon}
                </div>
                <h3 className="text-xl font-bold text-[#2C5282] mb-2">
                  {method.title}
                </h3>
                <p className="text-gray-600">{method.description}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-gray-600 mb-6 max-w-3xl mx-auto">
              Our methodology is designed to transform the way students learn,
              making education more effective, engaging, and result-oriented. We
              don't just teach; we ensure understanding and application.
            </p>
            <Link
              to="/methodology"
              className="inline-flex items-center text-[#2C5282] font-medium hover:text-[#1A365D] transition-colors duration-200"
            >
              Learn more about our teaching methodology
              <svg
                className="ml-2 w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M14 5l7 7m0 0l-7 7m7-7H3"
                />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* Faculty Profiles */}
      {/* <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.h2
              className="text-3xl font-extrabold text-[#2C5282] sm:text-4xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Meet Our Expert Faculty
            </motion.h2>
            <motion.p
              className="mt-4 max-w-2xl text-xl text-gray-600 mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              Learn from the best educators with proven track records
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {facultyProfiles
              .slice(0, showAllFaculty ? facultyProfiles.length : 3)
              .map((faculty, index) => (
                <motion.div
                  key={faculty.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 group"
                  onClick={() => setActiveFaculty(faculty)}
                >
                  <div className="aspect-w-3 aspect-h-4">
                    <img
                      src={faculty.image}
                      alt={faculty.name}
                      className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-[#2C5282] mb-1">
                      {faculty.name}
                    </h3>
                    <p className="text-gray-600 font-medium mb-3">
                      {faculty.designation}
                    </p>
                    <p className="text-gray-600 text-sm mb-4">
                      {faculty.school || ""}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {faculty.subjects &&
                        faculty.subjects.map((subject, i) => (
                          <span
                            key={i}
                            className="inline-block px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded"
                          >
                            {subject}
                          </span>
                        ))}
                    </div>
                    <button className="mt-4 text-[#2C5282] text-sm font-medium hover:text-[#1A365D] transition-colors duration-200 flex items-center">
                      View Profile
                      <svg
                        className="ml-1 w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </button>
                  </div>
                </motion.div>
              ))}
          </div>

          {!showAllFaculty && facultyProfiles.length > 3 && (
            <div className="text-center">
              <button
                onClick={() => setShowAllFaculty(true)}
                className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 shadow-sm text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200"
              >
                View All Faculty
                <svg
                  className="ml-2 w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
            </div>
          )}

          {activeFaculty && (
            <div
              className="fixed inset-0 z-50 overflow-y-auto"
              aria-labelledby="modal-title"
              role="dialog"
              aria-modal="true"
            >
              <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div
                  className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                  aria-hidden="true"
                  onClick={() => setActiveFaculty(null)}
                ></div>
                <span
                  className="hidden sm:inline-block sm:align-middle sm:h-screen"
                  aria-hidden="true"
                >
                  &#8203;
                </span>
                <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
                  <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                    <div className="sm:flex sm:items-start">
                      <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                        <div className="flex items-center justify-between mb-4">
                          <h3
                            className="text-2xl leading-6 font-bold text-[#2C5282]"
                            id="modal-title"
                          >
                            Faculty Profile
                          </h3>
                          <button
                            onClick={() => setActiveFaculty(null)}
                            className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                          >
                            <svg
                              className="h-6 w-6"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                          <div className="md:col-span-1">
                            <img
                              src={activeFaculty.image}
                              alt={activeFaculty.name}
                              className="w-full rounded-lg"
                            />
                          </div>
                          <div className="md:col-span-2">
                            <h4 className="text-xl font-bold text-[#2C5282] mb-1">
                              {activeFaculty.name}
                            </h4>
                            <p className="text-gray-600 font-medium mb-4">
                              {activeFaculty.designation}
                            </p>

                            <div className="space-y-4">
                              <div>
                                <h5 className="text-sm font-medium text-gray-500 mb-1">
                                  School
                                </h5>
                                <p className="text-gray-900">
                                  {activeFaculty.school ||
                                    "Independent Educator"}
                                </p>
                              </div>
                              <div>
                                <h5 className="text-sm font-medium text-gray-500 mb-1">
                                  Experience
                                </h5>
                                <p className="text-gray-900">
                                  {activeFaculty.experience}
                                </p>
                              </div>
                              <div>
                                <h5 className="text-sm font-medium text-gray-500 mb-1">
                                  Achievements
                                </h5>
                                <p className="text-gray-900">
                                  {activeFaculty.achievements}
                                </p>
                              </div>
                              <div>
                                <h5 className="text-sm font-medium text-gray-500 mb-1">
                                  Subjects
                                </h5>
                                <div className="flex flex-wrap gap-2 mt-2">
                                  {activeFaculty.subjects &&
                                    activeFaculty.subjects.map((subject, i) => (
                                      <span
                                        key={i}
                                        className="inline-block px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded"
                                      >
                                        {subject}
                                      </span>
                                    ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                    <button
                      type="button"
                      className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-[#2C5282] text-base font-medium text-white hover:bg-[#1A365D] focus:outline-none sm:ml-3 sm:w-auto sm:text-sm"
                      onClick={() => setActiveFaculty(null)}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section> */}

      {/* Success Stories */}
      {/* <section className="py-16 bg-gradient-to-br from-[#2C5282] to-[#1A365D] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.h2
              className="text-3xl font-extrabold sm:text-4xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Student Success Stories
            </motion.h2>
            <motion.p
              className="mt-4 max-w-2xl text-xl text-blue-200 mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              Hear from our students who achieved their academic goals
            </motion.p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {testimonials.slice(0, 3).map((testimonial, index) => (
              <motion.div
                key={testimonial.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white/10 backdrop-blur-sm rounded-lg overflow-hidden h-full flex flex-col border border-white/20"
              >
                <div className="p-6 flex-grow">
                  <div className="flex items-center mb-4">
                    <div className="mr-4 h-16 w-16 rounded-full overflow-hidden border-2 border-white/50">
                      <img
                        src={
                          testimonial.image ||
                          "/images/testimonial-placeholder.jpg"
                        }
                        alt={testimonial.author}
                        className="h-full w-full object-cover"
                      />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg">
                        {testimonial.author}
                      </h3>
                      <p className="text-blue-300 font-medium">
                        {testimonial.rank}
                      </p>
                    </div>
                  </div>
                  <p className="italic text-blue-100 mb-6">
                    "{testimonial.content}"
                  </p>
                </div>
                <div className="bg-white/5 p-6">
                  <h4 className="text-sm font-medium text-blue-300 mb-2">
                    Achievement Journey
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h5 className="text-xs font-semibold text-blue-200 mb-1">
                        Before Joining
                      </h5>
                      <p className="text-sm text-blue-100">
                        {testimonial.beforeJoining}
                      </p>
                    </div>
                    <div>
                      <h5 className="text-xs font-semibold text-blue-200 mb-1">
                        After Joining
                      </h5>
                      <p className="text-sm text-blue-100">
                        {testimonial.afterJoining}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/success-stories"
              className="inline-flex items-center text-white font-medium hover:text-blue-200 transition-colors duration-200"
            >
              View more success stories
              <svg
                className="ml-2 w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M14 5l7 7m0 0l-7 7m7-7H3"
                />
              </svg>
            </Link>
          </div>
        </div>
      </section> */}

      {/* Contact Points Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.h2
              className="text-3xl font-extrabold text-[#2C5282] sm:text-4xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Connect With Us
            </motion.h2>
            <motion.p
              className="mt-4 max-w-2xl text-xl text-gray-600 mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              Multiple ways to reach us for support and information
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {centers.map((center, index) => (
              <motion.div
                key={center.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200"
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src={center.image}
                    alt={center.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-[#2C5282] mb-2">
                    {center.name}
                  </h3>
                  <p className="text-gray-600 mb-4 flex items-start">
                    <svg
                      className="w-5 h-5 text-[#2C5282] mr-2 mt-0.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    <span>{center.address}</span>
                  </p>
                  {center.phone && (
                    <p className="text-gray-600 mb-4 flex items-start">
                      <svg
                        className="w-5 h-5 text-[#2C5282] mr-2 mt-0.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                        />
                      </svg>
                      <span>{center.phone}</span>
                    </p>
                  )}
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                      Available Services:
                    </h4>
                    <ul className="space-y-1">
                      {center.facilities.map((facility, i) => (
                        <li key={i} className="flex items-start">
                          <svg
                            className="w-4 h-4 text-[#2C5282] mt-0.5 mr-2"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                          <span className="text-sm text-gray-600">
                            {facility}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="py-16 bg-gray-50" id="faqs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.h2
              className="text-3xl font-extrabold text-[#2C5282] sm:text-4xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Frequently Asked Questions
            </motion.h2>
            <motion.p
              className="mt-4 max-w-2xl text-xl text-gray-600 mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              Find answers to common questions about our learning programs
            </motion.p>
          </div>

          <div className="max-w-3xl mx-auto divide-y divide-gray-200">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className="py-6"
              >
                <h3 className="text-lg font-medium text-[#2C5282]">
                  {faq.question}
                </h3>
                <p className="mt-2 text-gray-600">{faq.answer}</p>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-gray-600 mb-4">
              Still have questions? Feel free to contact us.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-[#2C5282] hover:bg-[#1A365D] transition-colors duration-300 shadow-md"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      {/* Schedule a Demo Section */}
      <section id="schedule-visit" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <motion.h2
                className="text-3xl font-extrabold text-[#2C5282] sm:text-4xl mb-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                Schedule a Free Demo Class
              </motion.h2>
              <motion.p
                className="text-lg text-gray-600 mb-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                Experience our teaching methodology firsthand. Schedule a free
                demo class and discover how we can help your child excel
                academically.
              </motion.p>
              <div className="space-y-4">
                <div className="flex items-start">
                  <svg
                    className="w-6 h-6 text-[#2C5282] mt-0.5 mr-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                    />
                  </svg>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">
                      Experience Our Classes
                    </h3>
                    <p className="text-gray-600">
                      See how our learning system works and interact with our
                      expert faculty
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <svg
                    className="w-6 h-6 text-[#2C5282] mt-0.5 mr-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                    />
                  </svg>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">
                      Meet Our Faculty
                    </h3>
                    <p className="text-gray-600">
                      Interact with our expert teachers and understand their
                      teaching style
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <svg
                    className="w-6 h-6 text-[#2C5282] mt-0.5 mr-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                    />
                  </svg>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">
                      Free Counseling
                    </h3>
                    <p className="text-gray-600">
                      Get personalized academic counseling for your child's
                      educational journey
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-gray-50 p-8 rounded-xl shadow-lg"
            >
              <h3 className="text-2xl font-bold text-[#2C5282] mb-6">
                Book Your Free Demo
              </h3>
              {demoFormSuccess ? (
                <div
                  className="bg-green-100 border border-green-400 text-green-700 px-4 py-6 rounded relative mb-6"
                  role="alert"
                >
                  <strong className="font-bold block mb-2">Success! </strong>
                  <span className="block">
                    Your demo class request has been submitted. We'll contact
                    you shortly to confirm the schedule.
                  </span>
                </div>
              ) : (
                <form className="space-y-6" onSubmit={handleDemoFormSubmit}>
                  {demoFormError && (
                    <div
                      className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative"
                      role="alert"
                    >
                      <span className="block sm:inline">{demoFormError}</span>
                    </div>
                  )}
                  <div>
                    <label
                      htmlFor="studentName"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Student Name*
                    </label>
                    <input
                      type="text"
                      id="studentName"
                      value={demoFormData.studentName}
                      onChange={handleDemoInputChange}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-[#2C5282] focus:ring-[#2C5282] sm:text-sm"
                      placeholder="Enter student's name"
                      required
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="parentEmail"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Parent Email*
                    </label>
                    <input
                      type="email"
                      id="parentEmail"
                      value={demoFormData.parentEmail}
                      onChange={handleDemoInputChange}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-[#2C5282] focus:ring-[#2C5282] sm:text-sm"
                      placeholder="Enter parent's email"
                      required
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="phone"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Phone Number*
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      value={demoFormData.phone}
                      onChange={handleDemoInputChange}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-[#2C5282] focus:ring-[#2C5282] sm:text-sm"
                      placeholder="Enter contact number"
                      required
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="grade"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Current Grade*
                    </label>
                    <select
                      id="grade"
                      value={demoFormData.grade}
                      onChange={handleDemoInputChange}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-[#2C5282] focus:ring-[#2C5282] sm:text-sm"
                      required
                    >
                      <option value="">Select grade</option>
                      <option value="8">8th Grade</option>
                      <option value="9">9th Grade</option>
                      <option value="10">10th Grade</option>
                    </select>
                  </div>
                  <div>
                    <label
                      htmlFor="program"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Interested Program*
                    </label>
                    <select
                      id="program"
                      value={demoFormData.program}
                      onChange={handleDemoInputChange}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-[#2C5282] focus:ring-[#2C5282] sm:text-sm"
                      required
                    >
                      <option value="">Select program</option>
                      <option value="english">
                        10th Standard English Medium
                      </option>
                      <option value="kannada">
                        10th Standard Kannada Medium
                      </option>
                      <option value="ncert">
                        10th Standard NCERT (Maths + Science)
                      </option>
                      <option value="navodaya">
                        Navodaya Entrance Preparation
                      </option>
                      <option value="morarji">
                        Morarji Entrance Preparation
                      </option>
                    </select>
                  </div>
                  <div>
                    <label
                      htmlFor="date"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Preferred Demo Date*
                    </label>
                    <input
                      type="date"
                      id="date"
                      value={demoFormData.date}
                      onChange={handleDemoInputChange}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-[#2C5282] focus:ring-[#2C5282] sm:text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="message"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Additional Message (Optional)
                    </label>
                    <textarea
                      id="message"
                      value={demoFormData.message}
                      onChange={handleDemoInputChange}
                      rows={3}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-[#2C5282] focus:ring-[#2C5282] sm:text-sm"
                      placeholder="Any specific requirements or questions"
                    ></textarea>
                  </div>
                  <div>
                    <button
                      type="submit"
                      disabled={demoFormSubmitting}
                      className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#2C5282] hover:bg-[#1A365D] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2C5282]"
                    >
                      {demoFormSubmitting ? (
                        <>
                          <svg
                            className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Processing...
                        </>
                      ) : (
                        "Schedule Demo Class"
                      )}
                    </button>
                  </div>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-gradient-to-br from-[#2C5282] to-[#1A365D] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <motion.h2
              className="text-3xl font-extrabold mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Start Your Academic Excellence Journey Today
            </motion.h2>
            <motion.p
              className="text-lg text-blue-100 mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              Join thousands of students who have achieved academic success with
              our comprehensive learning programs. Expert faculty, comprehensive
              curriculum, and personalized attention await you.
            </motion.p>
            <motion.div
              className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Link
                to="/register"
                className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-[#2C5282] bg-white hover:bg-gray-100 transition-colors duration-300 shadow-md"
              >
                Enroll Now
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-6 py-3 border-2 border-white text-base font-medium rounded-md text-white hover:bg-white/10 transition-colors duration-300"
              >
                Contact Us
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ClassroomPage;
